/**
 * RESUME — put a reload back on the screen it left.
 *
 * WHY THIS EXISTS. There is no router and the URL never changes (see
 * `history.ts`), so the screen lives in exactly one place: the store's `screen`
 * field, in memory. A phone evicts a backgrounded tab freely, and coming back
 * is then a full reload; pull-to-refresh is the same reload on purpose. Either
 * way `boot()` ran from scratch and a signed-in account landed on MY GAMES —
 * three taps deep into a game's page, or forty rows down BACKLOG, was gone.
 *
 * NOT A PORT. The prototype was a single document that was never reloaded, so
 * there are no prototype lines to cite. The register entry is
 * docs/PORTING-NOTES.md, "Introduced with the resume-on-reload fix".
 *
 * WHAT IS REMEMBERED: the screen, the game a `detail` page is on and the tab it
 * came from, the crewmate a `member` page is on, the three in-page tabs, and
 * the document scroll offset — for the account and the crew they were seen
 * under. Nothing else. Open sheets, armed destructive controls and drafts are
 * deliberately not part of it: a reload closes them the way a fresh entry
 * would, which is what a user who pulled to refresh expects.
 *
 * WHERE: `localStorage`, ONE key, keyed inside the value by user id. Not
 * `sessionStorage` — Chrome on Android and Safari on iOS both drop it when they
 * kill a backgrounded tab, which is the very event this module exists to
 * survive. The value is a position, not a credential: the worst it can leak is
 * "this account was last looking at BACKLOG".
 *
 * WHO WINS. `boot()` decides first — a reset, an invitation, a join link, a
 * `/c/<slug>` deep link or a crewless account all keep landing where they
 * always did. Only when boot's own answer is the default MY GAMES is the
 * snapshot consulted, and it is honoured only if it was written by THIS user
 * under THIS crew, is younger than `RESUME_TTL_MS`, and names things that still
 * exist (the game is in the catalog, the crewmate is in the crew, the round is
 * open). Anything else falls through to MY GAMES silently.
 *
 * THE WRITER NEVER TOUCHES STORAGE BEFORE `booted`. The store starts on `login`
 * with no user; a writer that reacted to that would delete the very snapshot
 * boot is about to read.
 *
 * AND IT NEVER DELETES ON ITS OWN AFTER BOOT EITHER (RS-10). Being on a screen
 * that is not resumable — `login`, `landing`, a reset — is a TRANSIENT state on
 * the way back in, not a statement that the remembered position is unwanted. A
 * cold wake lands there precisely BECAUSE the session needs re-establishing
 * (RS-09), and a writer that cleared the key on sight would throw the position
 * away in the seconds before the user signs back in. It does not take a reload
 * to trigger: `onHide` fires on the way to another app, so one glance at
 * something else while the login screen is up was enough. Found in a browser,
 * where a `pagehide` did exactly that.
 *
 * So only an EXPLICIT sign-out clears the key, through `clearResume` — an
 * intentional act, and the one that must not leave a stale position behind for
 * the next account on the same phone. Everything else leaves whatever is
 * stored alone; a snapshot for an account that never comes back is inert (it
 * is keyed by user id, so nobody else can land on it) and ages out at the TTL.
 *
 * SCROLL. `lib/scroll.ts` keeps every screen's offset in memory and lands on it
 * when the screen comes back; a reload empties that map. This module keeps the
 * offset of the view that was on screen and hands it back ONCE, through the
 * `takeResumeScroll` seed that `usePageScroll` consults. The scroll engine keeps
 * ownership of the jump and its one-frame re-assert (SC-05); this module only
 * knows the number. `detail` is not seeded — SC-02 says a pushed page never
 * remembers, and a reload of one lands at the top like any other arrival.
 *
 * TESTABILITY — the `history.ts` bargain. The snapshot policy is pure, storage is
 * injected (`StorageLike`, the same shape `LocalRepository` takes), and the
 * writer takes an injected `ResumeEnv`, so `resume.test.ts` runs in the repo's
 * NODE environment. Only `browserResumeEnv()` touches a real window.
 */

import type { GameId, Screen } from '../lib/types.ts';
import type { StorageLike } from '../repo/LocalRepository.ts';
import type { QdStore } from './types.ts';

export const RESUME_KEY = 'qd:resume';
/** A day. Older than this and the user has moved on; MY GAMES is the better guess. */
export const RESUME_TTL_MS = 24 * 60 * 60 * 1000;
export const RESUME_VERSION = 1;

/**
 * Screens a reload may land back on. Everything token-driven or pre-crew is
 * absent on purpose: `login`, `landing`, `startCrew`, `join`, `ask`, `waiting`,
 * `recover`, `reset` are all decided by boot from the session and the URL.
 */
const RESUMABLE: ReadonlySet<Screen> = new Set<Screen>([
  'mine',
  'backlog',
  'scores',
  'recs',
  'monitoring',
  'manage',
  'member',
  'tonight',
  'discover',
  'interests',
  'goty',
  'playtests',
  'detail',
  'crew',
  'profile',
  'round',
  // SCHEDULE's month (3071). A night page is not resumable: its key is not in
  // the snapshot, and a reload there lands on MY GAMES like any push.
  'schedule',
]);

/** A `from` a `detail` or `member` page may come back to: a page, not a push. */
const PAGE: ReadonlySet<Screen> = new Set<Screen>(
  [...RESUMABLE].filter((s) => s !== 'detail' && s !== 'member'),
);

export function isResumable(s: Screen): boolean {
  return RESUMABLE.has(s);
}

export interface ResumeSnapshot {
  v: typeof RESUME_VERSION;
  /** the account it was written for; a different one never sees it */
  uid: string;
  /** the crew the screen was showing; the server remembers the active crew, this checks it agrees */
  crewId: string;
  /** epoch ms of the last write */
  at: number;
  screen: Screen;
  from: Screen;
  detailId: GameId;
  memK: string;
  bkTab: string;
  rcTab: string;
  memTab: string;
  /** document scroll offset of the view on screen */
  y: number;
}

/** The store fields the snapshot is a function of. */
export type ResumeState = Pick<
  QdStore,
  | 'booted'
  | 'userId'
  | 'activeCrew'
  | 'screen'
  | 'from'
  | 'detailId'
  | 'memK'
  | 'bkTab'
  | 'rcTab'
  | 'memTab'
>;

// ---------------------------------------------------------------------------
// pure
// ---------------------------------------------------------------------------

/**
 * The snapshot the store is describing right now, or `null` when there is
 * nothing worth keeping — which the writer turns into "remove the key".
 */
export function snapshotOf(s: Readonly<ResumeState>, y: number, now: number): ResumeSnapshot | null {
  if (!s.booted || !s.userId || !s.activeCrew || !RESUMABLE.has(s.screen)) return null;
  return {
    v: RESUME_VERSION,
    uid: s.userId,
    crewId: s.activeCrew.id,
    at: now,
    screen: s.screen,
    from: s.from,
    detailId: s.detailId,
    memK: s.memK,
    bkTab: s.bkTab,
    rcTab: s.rcTab,
    memTab: s.memTab,
    y: Math.max(0, Math.floor(y)),
  };
}

/** Everything but `at` and `y` — the part a store change can alter. */
export function identityOf(snap: ResumeSnapshot): string {
  return [snap.uid, snap.crewId, snap.screen, snap.from, String(snap.detailId), snap.memK, snap.bkTab, snap.rcTab, snap.memTab].join(' ');
}

/** A stored value, if it is one of ours and still fresh; otherwise `null`. */
export function parseResume(raw: string | null, now: number): ResumeSnapshot | null {
  if (!raw) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof v !== 'object' || v === null) return null;
  const o = v as Record<string, unknown>;
  if (o.v !== RESUME_VERSION) return null;
  if (typeof o.uid !== 'string' || !o.uid) return null;
  if (typeof o.crewId !== 'string' || !o.crewId) return null;
  if (typeof o.at !== 'number' || !Number.isFinite(o.at)) return null;
  if (now - o.at > RESUME_TTL_MS || o.at > now + 60_000) return null;
  if (typeof o.screen !== 'string' || !RESUMABLE.has(o.screen as Screen)) return null;
  if (typeof o.from !== 'string' || !PAGE.has(o.from as Screen)) return null;
  if (typeof o.detailId !== 'number' && typeof o.detailId !== 'string') return null;
  if (typeof o.memK !== 'string') return null;
  if (typeof o.bkTab !== 'string' || typeof o.rcTab !== 'string' || typeof o.memTab !== 'string') return null;
  const y = typeof o.y === 'number' && Number.isFinite(o.y) ? Math.max(0, Math.floor(o.y)) : 0;
  return {
    v: RESUME_VERSION,
    uid: o.uid,
    crewId: o.crewId,
    at: o.at,
    screen: o.screen as Screen,
    from: o.from as Screen,
    detailId: o.detailId as GameId,
    memK: o.memK,
    bkTab: o.bkTab,
    rcTab: o.rcTab,
    memTab: o.memTab,
    y,
  };
}

// ---------------------------------------------------------------------------
// storage
// ---------------------------------------------------------------------------

/** `localStorage` when there is one that works; `null` in node and in a browser that throws on it. */
export function browserResumeStorage(): StorageLike | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    // Safari's private mode used to throw on write, not on read — probe both.
    const k = RESUME_KEY + ':probe';
    localStorage.setItem(k, '1');
    localStorage.removeItem(k);
    return localStorage;
  } catch {
    return null;
  }
}

export function readResume(storage: StorageLike | null, now: number): ResumeSnapshot | null {
  if (!storage) return null;
  try {
    return parseResume(storage.getItem(RESUME_KEY), now);
  } catch {
    return null;
  }
}

function writeResume(storage: StorageLike, snap: ResumeSnapshot): void {
  try {
    storage.setItem(RESUME_KEY, JSON.stringify(snap));
  } catch {
    // Quota or a storage that has gone read-only. Best-effort: the in-memory
    // app is unaffected; only surviving the next reload is lost.
  }
}

/**
 * Forget the remembered position — the ONLY thing that removes it (RS-10).
 *
 * Called from `signOut`, because signing out is the one act that says the
 * position is unwanted: the next person to open the app on this phone must not
 * land on the last one's screen. Everything else that merely finds itself on a
 * non-resumable screen leaves the key alone, or a cold wake would wipe the
 * position in the seconds before the user signs back in (RS-09).
 */
export function clearResume(storage: StorageLike | null): void {
  if (!storage) return;
  try {
    storage.removeItem(RESUME_KEY);
  } catch {
    /* see writeResume */
  }
}

// ---------------------------------------------------------------------------
// apply — called from boot, once, at the landing decision
// ---------------------------------------------------------------------------

/** The store actions a restore drives — the same ones a tap would. */
export type ResumeActions = Pick<
  QdStore,
  | 'nav'
  | 'openGame'
  | 'openMember'
  | 'setBkTab'
  | 'setRcTab'
  | 'setMemTab'
  | 'rOpen'
  | 'gById'
  | 'user'
  | 'round'
  | 'userId'
  | 'activeCrew'
>;

/**
 * The scroll seed the accepted snapshot left for `lib/scroll.ts`, consumed by
 * the first `takeResumeScroll` that asks for its key. Module state rather than
 * store state: it is a one-shot hand-off between two modules, not something a
 * screen renders.
 */
let pendingScroll: { key: string; y: number } | null = null;

/**
 * Drive the store to the snapshot's screen, through the same actions a tap
 * would take, so every per-screen entry behaviour (`nav`'s drafts, `openGame`'s
 * fetches, `openMember`'s lists) runs exactly as it does on the way in.
 *
 * Returns whether it moved. It is a no-op — and MY GAMES stays — when the
 * snapshot belongs to another account or another crew, or names something that
 * no longer exists. The check against the store is what makes an old snapshot
 * safe: the server already answered "who is this and which crew", and this
 * only ever picks a screen inside that answer.
 */
export function applyResume(s: ResumeActions, snap: ResumeSnapshot | null): boolean {
  const reject = (): false => {
    // A rejected snapshot must not leave an earlier accepted one's seed behind.
    pendingScroll = null;
    return false;
  };
  if (!snap) return reject();
  if (snap.uid !== s.userId) return reject();
  if (!s.activeCrew || snap.crewId !== s.activeCrew.id) return reject();

  /**
   * Planted BEFORE the navigation, so the hand-off does not depend on when
   * React commits the screen change — the shell's layout effect asks the seed
   * on that commit, whether it lands synchronously or a task later.
   *
   * An ACCEPTED snapshot with no offset leaves an existing seed alone rather
   * than clearing it. Dev-only, but it made local testing lie: React
   * StrictMode runs `App`'s boot effect twice, and the second `boot()` reads a
   * snapshot the writer has already rewritten from the first boot's landing —
   * same account, same crew, same screen, `y: 0`. Clearing on that would drop
   * the 900 the first boot planted a moment earlier, and the page would land
   * at 0 in dev and at 900 on staging. Found by a `scrollTo` trace in a real
   * browser; the unit test (one boot, no React) passed throughout.
   */
  const seed = (screen: Screen): void => {
    // SC-02: a pushed detail page lands at the top, reload or not.
    if (screen === 'detail' || snap.y <= 0) return;
    pendingScroll = { key: screen, y: snap.y };
  };

  switch (snap.screen) {
    case 'detail': {
      if (!s.gById(snap.detailId)) return reject();
      s.nav(snap.from);
      s.openGame(snap.detailId);
      return true;
    }
    case 'member': {
      if (!s.user(snap.memK)) return reject();
      seed('member');
      s.nav(snap.from);
      s.openMember(snap.memK);
      s.setMemTab(snap.memTab);
      return true;
    }
    case 'round': {
      // `rOpen` is a raw screen set; without an open round the screen is a
      // blank wheel, and TONIGHT is where that state's own code sends you.
      if (!s.round) return reject();
      seed('round');
      s.rOpen();
      return true;
    }
    default: {
      seed(snap.screen);
      s.nav(snap.screen);
      if (snap.screen === 'backlog') s.setBkTab(snap.bkTab as QdStore['bkTab']);
      if (snap.screen === 'recs') s.setRcTab(snap.rcTab as QdStore['rcTab']);
      return true;
    }
  }
}

/**
 * The seed for `lib/scroll.ts`: the remembered offset for `key`, once. Any
 * other key — or a second ask — gets `null`, so a snapshot that boot rejected
 * can never scroll a screen it was not written for.
 */
export function takeResumeScroll(key: string): number | null {
  if (!pendingScroll || pendingScroll.key !== key) return null;
  const y = pendingScroll.y;
  pendingScroll = null;
  return y;
}

/** Test seam. */
export function resetResumeScroll(): void {
  pendingScroll = null;
}

// ---------------------------------------------------------------------------
// the writer
// ---------------------------------------------------------------------------

export interface ResumeEnv {
  storage: StorageLike | null;
  now: () => number;
  /** current document scroll offset */
  y: () => number;
  /** passive scroll subscription; returns its teardown */
  onScroll: (fn: () => void) => () => void;
  /** the page going away or to the background — the last chance to write */
  onHide: (fn: () => void) => () => void;
  /** a coalescing timer for scroll writes; returns a cancel */
  later: (fn: () => void, ms: number) => () => void;
}

export interface ResumeStore {
  getState: () => ResumeState;
  subscribe: (listener: () => void) => () => void;
}

/** Scroll writes are coalesced to this; a store change writes at once. */
export const RESUME_SCROLL_MS = 250;

export function browserResumeEnv(): ResumeEnv {
  return {
    storage: browserResumeStorage(),
    now: () => Date.now(),
    y: () => window.scrollY,
    onScroll: (fn) => {
      window.addEventListener('scroll', fn, { passive: true });
      return () => window.removeEventListener('scroll', fn);
    },
    onHide: (fn) => {
      // `pagehide` is the reliable one on iOS; `visibilitychange` → hidden is
      // what fires when the app is backgrounded rather than navigated away.
      const vis = (): void => {
        if (document.visibilityState === 'hidden') fn();
      };
      window.addEventListener('pagehide', fn);
      document.addEventListener('visibilitychange', vis);
      return () => {
        window.removeEventListener('pagehide', fn);
        document.removeEventListener('visibilitychange', vis);
      };
    },
    later: (fn, ms) => {
      const id = setTimeout(fn, ms);
      return () => clearTimeout(id);
    },
  };
}

let active: (() => void) | null = null;

/**
 * Keep the stored snapshot in step with the store and the scroll offset.
 *
 * Idempotent like `initHistorySync`: a second call while one is live is a
 * no-op. Writes happen on a change of the snapshot's identity (at once) and on
 * scroll (coalesced), and on hide regardless, so backgrounding the tab a moment
 * after scrolling does not lose the offset to the timer.
 */
export function initResumeSync(store: ResumeStore, env: ResumeEnv): void {
  if (active) return;
  if (!env.storage) return;
  const storage = env.storage;

  /** identity of the last snapshot written; `''` = nothing / removed */
  let lastId = '';
  let cancelScroll: (() => void) | null = null;

  const write = (): void => {
    cancelScroll?.();
    cancelScroll = null;
    const s = store.getState();
    // Before boot the store says `login` and no user, and that must not be
    // taken as "remove" — it is "not yet".
    if (!s.booted) return;
    const snap = snapshotOf(s, env.y(), env.now());
    lastId = snap ? identityOf(snap) : '';
    // NOTHING TO SAY IS NOT "FORGET IT" (RS-10). A non-resumable screen is a
    // transient state on the way back in — see the header. Leave whatever is
    // stored alone; `clearResume` is the only thing that removes it.
    if (snap) writeResume(storage, snap);
  };

  const onStore = (): void => {
    const s = store.getState();
    if (!s.booted) return;
    const snap = snapshotOf(s, env.y(), env.now());
    const id = snap ? identityOf(snap) : '';
    if (id === lastId) return;
    write();
  };

  const onScroll = (): void => {
    if (lastId === '' || cancelScroll) return;
    cancelScroll = env.later(write, RESUME_SCROLL_MS);
  };

  const offStore = store.subscribe(onStore);
  const offScroll = env.onScroll(onScroll);
  const offHide = env.onHide(write);

  // The store may already be past boot (a hot reload of main.tsx); adopt it.
  onStore();

  active = () => {
    offStore();
    offScroll();
    offHide();
    cancelScroll?.();
    cancelScroll = null;
  };
}

/** Test seam — drops the subscriptions and lets `initResumeSync` run again. */
export function resetResumeSync(): void {
  active?.();
  active = null;
}
